import Bold from '@tiptap/extension-bold'
import BulletList from '@tiptap/extension-bullet-list'
import Heading from '@tiptap/extension-heading'
import Italic from '@tiptap/extension-italic'
import Link from '@tiptap/extension-link'
import ListItem from '@tiptap/extension-list-item'
import Mention from '@tiptap/extension-mention'
import OrderedList from '@tiptap/extension-ordered-list'
import Paragraph from '@tiptap/extension-paragraph'
import Strike from '@tiptap/extension-strike'
import Text from '@tiptap/extension-text'
import Underline from '@tiptap/extension-underline'
import { Editor, JSONContent } from '@tiptap/react'
import {
  defaultMarkdownSerializer,
  MarkdownSerializer,
  MarkdownSerializerState
} from 'prosemirror-markdown'
import { Node } from 'prosemirror-model'

import { isPlainURL } from '../utils/isPlainUrl'

const serializerMarks: ConstructorParameters<typeof MarkdownSerializer>['1'] = {
  [Bold.name]: {
    open: '**',
    close: '**',
    mixable: true
  },
  [Strike.name]: {
    open: '~~',
    close: '~~',
    mixable: true
  },
  [Italic.name]: {
    open: '_',
    close: '_',
    mixable: true
  },
  [Underline.name]: {
    open: '<ins>',
    close: '</ins>',
    mixable: true
  },
  [Link.name]: {
    open(_state, mark, parent, index) {
      return isPlainURL(mark, parent, index, 1) ? '<' : '['
    },
    close(state, mark, parent, index) {
      const href = mark.attrs.canonicalSrc || mark.attrs.href

      return isPlainURL(mark, parent, index, -1)
        ? '>'
        : `](${state.esc(href)}${
            mark.attrs.title
              ? ` ${
                  //@ts-expect-error function .quote actually exists in the package
                  state.quote(mark.attrs.title)
                }`
              : ''
          })`
    }
  }
}

const serializerNodes: {
  [node: string]: (
    state: MarkdownSerializerState,
    node: Node,
    parent: Node,
    index: number
  ) => void
} = {
  [Text.name]: defaultMarkdownSerializer.nodes.text,
  [Heading.name]: defaultMarkdownSerializer.nodes.heading,
  [Paragraph.name]: defaultMarkdownSerializer.nodes.paragraph,
  [BulletList.name]: defaultMarkdownSerializer.nodes.bullet_list,
  [ListItem.name]: defaultMarkdownSerializer.nodes.list_item,
  [OrderedList.name]: (state, node) => {
    const { parens } = node.attrs
    const start = node.attrs.start || 1
    const maxW = String(start + node.childCount - 1).length
    const space = state.repeat(' ', maxW + 2)
    const delimiter = parens ? ')' : '.'
    state.renderList(node, space, (i) => {
      const nStr = String(start + i)
      return `${state.repeat(' ', maxW - nStr.length) + nStr}${delimiter} `
    })
  },
  [Mention.name]: (state, node) => {
    const [mentionedType, id, name] = node.attrs.id.split('/')
    state.text(`[@${name}](${mentionedType}/${id})`, false)
    state.text(' ')
  }
}

export function markdownSerializer(
  schema: Editor['schema'],
  content: JSONContent
) {
  const proseMirrorDocument = schema.nodeFromJSON(content)
  const serializer = new MarkdownSerializer(serializerNodes, serializerMarks)
  return serializer.serialize(proseMirrorDocument, {
    tightLists: true
  })
}
